import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Form, useFormikContext } from 'formik';
import { useCallback, useRef } from 'react';

import type { VesselMessage } from '../../../../types/VesselMessage';
import { Callout } from '../../../cdl/Callout/Callout';
import { Text } from '../../../cdl/Text/Text';
import { queries } from '../../../common/api/queryKeys/queries';
import { FormikCustomerCompanySelect } from '../../../common/form-elements/formik/FormikCustomerCompanySelect';
import { addUtcDays, endOfUtcDayIso, startOfUtcDayIso } from '../../../common/helpers/dateFns.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { Page } from '../../../common/Page/Page';
import { PageGrid } from '../../../common/PageGrid';
import { Box } from '../../../common/ui/Box';
import { FormikVesselSelect } from '../../../vessel/FormikVesselSelect';
import type { PriceCompareFormValues } from '../types/PriceCompareFormValues';
import { usePriceCompare } from '../usePriceCompare';
import { calculatePriceCompareItem } from '../utils/calculatePriceCompareItem';
import { createPriceComparePort } from '../utils/createPriceComparePort';

import { ItemsTable } from './ItemsTable';
import { PriceInfoSection } from './PriceInfoSection';

export const CompareOverviewForm = () => {
    const role = useRole();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const selectedVesselIdRef = useRef<string | undefined>(undefined);
    const { values, setFieldValue } = useFormikContext<PriceCompareFormValues>();
    const { priceMap, isFetching } = usePriceCompare({ values });

    const handleCustomerChange = (customerId: string) => {
        if (customerId === values.customerId) {
            return;
        }

        selectedVesselIdRef.current = undefined;
        setFieldValue('vesselId', '');
        setFieldValue('vessel', undefined);
        setFieldValue('portInformation', [createPriceComparePort()]);
    };

    const handleVesselChange = useCallback(
        async (vessel: VesselMessage | null) => {
            selectedVesselIdRef.current = vessel?.id;
            setFieldValue('vessel', vessel);

            if (!vessel) {
                setFieldValue('portInformation', [createPriceComparePort()]);
                return;
            }

            const scheduleList = await queryClient.fetchQuery(
                queries.schedules.list({
                    imo: vessel.imo,
                    etdFrom: startOfUtcDayIso(new Date()),
                    etdTo: endOfUtcDayIso(addUtcDays(new Date(), 120)),
                    customerGroupId: vessel.customer?.customerGroupId,
                })
            );

            if (selectedVesselIdRef.current !== vessel.id) {
                return;
            }

            const scheduledPorts = scheduleList.flatMap((schedule) =>
                schedule.port ? [createPriceComparePort(schedule.port.id)] : []
            );

            if (scheduledPorts.length) {
                setFieldValue('portInformation', scheduledPorts);
            }
        },
        [queryClient, setFieldValue]
    );

    const handleEnquiryClick = (portId?: string) => {
        if (!portId) {
            return;
        }

        navigate({
            to: '/orders/new',
            state: {
                items: values.items.map(calculatePriceCompareItem),
                portId,
            },
        });
    };

    return (
        <Page>
            <PageGrid>
                <Text variant="headline" as="h1">
                    {translate('compare.title')}
                </Text>

                <Callout
                    heading={translate('compare.page.bar.headline')}
                    description={translate('compare.page.bar.description')}
                    Icon={IconInfoCircle}
                />

                <Form>
                    <Box display="flex" gap={8}>
                        {!role.isOneCompanyUser() ? (
                            <Box minWidth="300px" maxWidth="33%">
                                <FormikCustomerCompanySelect
                                    name="customerId"
                                    label={translate('compare.company')}
                                    placeholder={translate('compare.placeholder.company')}
                                    markAsRequired
                                    onCustomerChange={handleCustomerChange}
                                />
                            </Box>
                        ) : null}
                        <Box display="flex" gap={8} alignItems="flex-end">
                            <Box minWidth="300px" maxWidth="33%">
                                <FormikVesselSelect
                                    name="vesselId"
                                    label={translate('compare.vessel')}
                                    placeholder={translate('compare.placeholder.vessel')}
                                    customerIds={values.customerId ? [values.customerId] : []}
                                    disabled={!values.customerId}
                                    isClearable
                                    onVesselSelect={handleVesselChange}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box marginTop={40}>
                        <ItemsTable />
                    </Box>

                    <Box marginTop={40}>
                        <PriceInfoSection
                            isFetching={isFetching}
                            priceMap={priceMap}
                            onEnquiryButtonClick={handleEnquiryClick}
                        />
                    </Box>
                </Form>
            </PageGrid>
        </Page>
    );
};
